// #MODULE - Coremetrics
// > Authors: Various, Michael Moir
// >
// > Create Date: unknown
// > Last Modified Date: January 2, 2015
// >
// ##DESCRIPTION: This module handles various Coremetics tagging and tracking functions

import _ from 'underscore';
import coremetricsAttributes from '@component/common/src/util/CoremetricsAttributes';
import ClientSideStorage from '@component/common/src/util/ClientSideStorage';
import UserStatus from '@component/common/src/util/UserStatus';


// Coremetrics Analytics
//----------------

// The coremetrics file is loaded from http://libs.coremetrics.com/eluminate.js
// Current version : (Build # 4.14.39 Date/Time: 20130930-12:54)
// Custom coremetrics functions : http://assets.macys.com/navapp/web20/assets/script/coremetrics/cmcustom.js

// `cmAnalytics` encapsulates the coremetrics analytics api
const cmAnalytics = {};

// `createAttributeString` uses the `coremetricsAttributes` module to create coremetrics formatted attribute string
// required `arr` `Array` of attributes
function createAttributeString(arr) {
  // check data type
  if (!arr || Object.prototype.toString.call(arr) !== '[object Array]') {
    return '';
  }
  coremetricsAttributes.reset();
  let i;
  const alen = arr.length;
  // loop over attributes to create the coremetrics attributes string
  for (i = 0; i < alen; i += 1) {
    coremetricsAttributes.addAttribute(arr[i], i + 1);
  }

  return coremetricsAttributes.attributeToString();
}

// `productViewTag` fires the cmCreateProductviewTag(productID, productName, categoryID, cm_ven, cm_cat,
// cm_pla, cm_ite,linkShareID, custID, refURL, cmCrossSell,totalReviewCount,avgRating,numberRatingsOnlyReviews,buyAgainPercentage,attributes)
// required `data.productID`
// needs `data.productName`
// needs `data.crossSell`
// needs `data.attributes` is an array of attributes
cmAnalytics.productViewTag = (data) => {
  const cmAttrs = createAttributeString(data.attributes);
  // future impl should make this generic enough so that other cases can be handled
  /* istanbul ignore else */
  if (window.cmCreateProductviewTag) {
    window.cmCreateProductviewTag(
      data.productID, data.productName, data.categoryID, data.partnerId, '', data.bannerId,
      '', data.linkShareId, data.macysOnlineCookie, data.bhrfCookie, data.crossSell, '', '', '', '', cmAttrs,
    );
  }
};

// `elementTag` fires the `cmCreatePageElementTag(elementID, elementCategory,attributes)`
// required `data.elementID`
// needs `data.elementCategory`
// needs `data.attributes` is an array of attributes
cmAnalytics.elementTag = (data) => {
  const cmAttrs = createAttributeString(data.attributes);
  /* istanbul ignore else */
  if (window.cmCreatePageElementTag) {
    window.cmCreatePageElementTag(data.elementID, data.elementCategory, cmAttrs);
  }
};

function addAttribute35ToPageViewTag(_arr) {
  const arr = _arr;
  if (!arr || Object.prototype.toString.call(arr) !== '[object Array]') {
    return '';
  }
  if (UserStatus.isSoftSignedIn()) {
    arr[34] = 'Soft Signed-In';
  } else if (UserStatus.isSignedIn()) {
    arr[34] = 'Signed-In';
  } else {
    arr[34] = 'Guest';
  }
  return null;
}

// `pageViewTag` fires the `cmCreatePageviewTag(pageID, categoryID, searchString, searchResults, attributes, extraFields)`
// required `data` is an object
// required `data.pageID`
cmAnalytics.pageViewTag = (data) => {
  addAttribute35ToPageViewTag(data.attributes);
  const cmAttrs = createAttributeString(data.attributes);
  /* istanbul ignore else */
  if (window.cmCreatePageviewTag) {
    window.cmCreatePageviewTag(
      data.pageID, data.categoryID, data.searchString, data.searchResults, data.partnerID,
      '', data.bannerID, data.productID, data.linkShareID, data.userID, data.refURL, cmAttrs, data.extraFields,
    );
  }
};


// `bazaarViewTag` calls the `cmCreateBazaarViewTag(productID, productName,categoryID)`
// required `data`
// required `data.productID`
// needs `data.productName`
// needs `data.categoryID`
cmAnalytics.bazaarViewTag = (data) => {
  /* istanbul ignore else */
  if (window.cmCreateBazaarViewTag) {
    window.cmCreateBazaarViewTag(data.productID, data.productName, data.categoryID);
  }
};

// `shopAction5Tag` calls the `cmCreateShopAction5Tag(productID, productName, productQuantity, productPrice,
// categoryID,MasterProductID, MasterProductName, MasterCatID, IsMaster, cmCrossSell,attributes)`
// required `data`
// required `data.productID`
cmAnalytics.shopAction5Tag = (data) => {
  const cmAttrs = createAttributeString(data.attributes);
  /* istanbul ignore else */
  if (window.cmCreatePageElementTag) {
    window.cmCreateShopAction5Tag(
      data.productID, data.productName, data.productQuantity, data.productPrice,
      data.categoryID, data.MasterProductID, data.MasterProductName, data.MasterCatID, data.IsMaster, data.cmCrossSell, cmAttrs,
    );
  }
  /* istanbul ignore else */
  if (window.cmDisplayShop5s) {
    window.cmDisplayShop5s();
  }
};

// `shopAction9Tag` calls the `cmCreateShopAction5Tag(productID, productName, productQuantity, productPrice,
// categoryID,MasterProductID, MasterProductName, MasterCatID, IsMaster, cmCrossSell,attributes)`
// required `data`
// required `data.productID`
cmAnalytics.shopAction9Tag = (data) => {
  const cmAttrs = createAttributeString(data.attributes);
  /* istanbul ignore else */
  if (window.cmCreateShopAction9Tag) {
    window.cmCreateShopAction9Tag(
      data.productID, data.productName, data.productQuantity, data.productPrice,
      data.customerID, data.orderID, data.orderTotal, data.categoryID, data.MasterProductID, data.MasterProductName,
      data.MasterCatID, data.IsMaster, data.cmCrossSell, cmAttrs,
    );
  }
  /* istanbul ignore else */
  if (window.cmDisplayShop9s) {
    window.cmDisplayShop9s();
  }
};

// `cmAnalytics` calls `cmCreateMasterMemberTag(MasterProductID, MasterProductName, MasterCatID, IsMaster, custID)`
// required `data`
// required `data.masterProductID`
cmAnalytics.masterMemberTag = (data) => {
  /* istanbul ignore else */
  if (window.cmCreateMasterMemberTag) {
    window.cmCreateMasterMemberTag(data.masterProductID, data.masterProductName, data.masterCatID, data.isMaster, data.custID);
  }
};

// conversion event tag
cmAnalytics.ConversionEventTag = (data) => {
  const cmAttrs = createAttributeString(data.attributes);
  /* istanbul ignore else */
  if (window.cmCreateConversionEventTag) {
    window.cmCreateConversionEventTag(
      data.conversionTagEventId, data.conversionTagEventActionType,
      data.conversionTagEventCategoryID, data.conversionTagEventPoints, cmAttrs,
    );
  }
};

cmAnalytics.linkClickTag = (data) => {
  /* istanbul ignore else */
  if (window.cmCreateManualLinkClickTag) {
    window.cmCreateManualLinkClickTag(data.href, data.name, data.pageID);
  }
};

cmAnalytics.CustomTag = (data) => {
  /* istanbul ignore else */
  if (window.cmCreateUserErrorTag) {
    window.cmCreateUserErrorTag(
      data.pageID, data.categoryID, data.mMsgCode, data.mMsgType, data.mMsgClass,
      data.mMsgDesc, data.mServerName, data.mCloneName,
    );
  }
};

cmAnalytics.RegistrationTag = (data) => {
  /* istanbul ignore else */
  if (window.cmCreateRegistrationTag) {
    window.cmCreateRegistrationTag(
      data.customerID, data.email, data.city, data.state, data.zipCode, data.customerGender,
      data.newsletterName, data.subscribe,
    );
  }
};

// order tag
cmAnalytics.OrderTag = (data) => {
  const cmAttrs = createAttributeString(data.attributes);
  /* istanbul ignore else */
  if (window.cmCreateOrderTag) {
    window.cmCreateOrderTag(
      data.orderID, data.orderTotal, data.orderShipping, data.customerID, data.customerCity,
      data.customerState, data.customerZIP, data.custID, cmAttrs,
    );
  }
};

// `cmRecRequest` calls the `cmRecRequest(zoneId, productId, categoryId, randomizeParam, searchTermParam) eluminate function`
// required `data`
// required `data.zoneId`
// required `data.categoryId`
cmAnalytics.cmRecRequest = (data) => {
  /* istanbul ignore else */
  if (window.cmRecRequest) {
    window.cmRecRequest(data.zoneId, data.productId, data.categoryId, data.randomizeParam, data.searchTermParam);
  }
};

// `cmDisplayRecs` calls the `cmDisplayRecs() eluminate function`
cmAnalytics.cmDisplayRecs = () => {
  /* istanbul ignore else */
  if (window.cmDisplayRecs) {
    window.cmDisplayRecs();
  }
};

cmAnalytics.productViewDataToPageViewData = (data) => {
  const convertedData = _.omit(data, 'attributes');
  convertedData.pageID = `PRODUCT: ${convertedData.productName} (${convertedData.productID})`;
  return convertedData;
};

/* THIS METHOD IS CURRENTLY EXPERIMENTAL - NO NOT LEVERAGE
   *
   * experimentLinkClickTag(data) - This method fires a manual link
   * click tag with site promotion addition used to track asynchronous
   * experimentation.
   *
   * ?cm_sp=EXPERIMENT-_-<data.expName>-_-<data.expValue>
   *
   * Expected optional data
   * data.name          - Link name (default: 'EXPERIMENT_LINK')
   * data.href          - Link href (default: Current relative URL with any site promotion replaced)
   * data.pageID        - Current page CM page ID. (default: 'UNKNOWN' or pulled from BrightTag values)
   * data.sessionBased  - Should this link fire only once per session (default: false)
   *
   * REQUIRED data
   * data.expName       - Identifier for experiment, 2nd value in site promotion
   * data.expValue      - Identifier for segment of experiment, 3rd value of site promotion
   * */
cmAnalytics.experimentLinkClickTag = (_data) => {
  let data = _data;
  /* istanbul ignore else */
  if (!_.isUndefined(data.expName) && !_.isUndefined(data.expValue)) {
    const sitePromoValue = `EXPERIMENT-_-${data.expName}-_-${data.expValue}`;
    const generatedHref = `${document.location.pathname}/EXP?cm_sp=${sitePromoValue}`;
    const generatedPageID = 'UNKNOWN';
    /* istanbul ignore else */

    // Setting defaults if they don't have values already
    data = _.defaults(data, {
      name: 'EXPERIMENT_LINK', href: generatedHref, pageID: generatedPageID, sessionBased: false,
    });

    const existsInSessionStorage = ClientSideStorage.getSession(sitePromoValue);
    /* istanbul ignore else */
    if (!data.sessionBased || existsInSessionStorage === null) {
      /* istanbul ignore else */
      if (cmAnalytics && cmAnalytics.linkClickTag) {
        cmAnalytics.linkClickTag(data);
      }
      /* istanbul ignore else */
      if (data.sessionBased) {
        ClientSideStorage.setSession(sitePromoValue, new Date().toString());
      }
    }
  }
};

export default cmAnalytics;
